import { Loader2Icon, PlusIcon, Trash2 } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import {
  Control,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
} from "react-hook-form";

import { MultiSelectProps, SelectMultiple } from "../CustomMultiSelect";
import { P } from "../typography";

import FormWrapper from "./FormWrapper";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from "@/components/ui/command";
import { Side } from "@/components/ui/customChervonIcon";
import { FormControl, FormField } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import * as Popover from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TAny } from "@/interfaces/globals";
import { IbtnVariant, LabelValue } from "@/interfaces/interfaces";
import { cn } from "@/lib/utils";
import Icons from "@/utils/Icons";

export type Option = {
  label: string;
  url?: string;
  value: string | { label: string; id: string };
};

type SelectSingle<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = ClassName & {
  options?: Option[];
  groupedOptions?: {
    header: string;
    options: Option[];
  }[];
  placeholder: string;
  field?: ControllerRenderProps<TFieldValues, TName>;
  isLoading?: boolean;
  onFieldValueChange?: (value: string, label?: string) => void;
  defaultValue?: string;
  addButton?:
    | {
        label?: string;
        variant?: IbtnVariant;
        className?: string;
        handleClick?: () => void;
        icon?: string;
      }
    | false;
  deleteAction?: (id: string) => void;
  searchable?: boolean;
  disabled?: boolean;
  side?: Side;
  allowDuplicate?: boolean;
};

// Unusable for now
const FormSelectSingles = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: SelectSingle<TFieldValues, TName> & {
    withoutForm?: boolean;
    customTrigger?: React.ReactNode;
  },
): React.JSX.Element => {
  const {
    placeholder,
    options,
    field,
    className,
    isLoading,
    onFieldValueChange,
    defaultValue,
    withoutForm,
    customTrigger,
    addButton,
    deleteAction,
    searchable = true,
  } = props;
  const [searchedText, setSearchedText] = useState("");

  const filterOptions = options?.filter((option) =>
    option?.label?.toLowerCase()?.includes(searchedText?.toLowerCase()),
  );

  const Comp = withoutForm ? React.Fragment : FormControl;

  const getContent = (): React.JSX.Element => {
    if (isLoading) {
      return (
        <p>
          <Loader2Icon className='animate-spin' />
        </p>
      );
    }

    if (filterOptions?.length) {
      return (
        <>
          <ScrollArea className='overflow-y-auto max-h-32 scrollbar-thin'>
            {filterOptions?.map((option) => {
              const optionValue =
                typeof option?.value === "object"
                  ? option?.value?.id
                  : option?.value;

              return (
                <div className='relative space-y-2' key={optionValue}>
                  <SelectItem
                    value={optionValue}
                    selected={field?.value === optionValue}
                    className={cn("px-2")}
                  >
                    {option.label}
                  </SelectItem>
                  {deleteAction && (
                    <div
                      className='absolute top-0 p-1 cursor-pointer right-3'
                      onClick={(e) => {
                        e.preventDefault();
                        deleteAction(optionValue);
                      }}
                    >
                      <Trash2 name='trash' color='red' size={18} />
                    </div>
                  )}
                </div>
              );
            })}
          </ScrollArea>
          {addButton && (
            <Button
              type='button'
              variant={`${addButton?.variant ? addButton.variant : "secondary"}`}
              className={cn(
                "flex justify-center items-center align-center w-full",
                addButton?.className,
              )}
              onClick={addButton.handleClick}
            >
              {addButton.icon ? (
                <Icons name={addButton.icon} size={18} color='#fff' />
              ) : (
                <PlusIcon size={18} strokeWidth={1.8} className='mx-1' />
              )}
              <p className={`${addButton.icon && "px-2"}`}>
                {addButton?.label}
              </p>
            </Button>
          )}
        </>
      );
    }

    return (
      <div>
        <P className='py-2 text-sm text-center'>No Options</P>
        {addButton && (
          <Button
            type='button'
            variant={`${addButton?.variant ? addButton.variant : "secondary"}`}
            className={cn(
              "flex justify-center items-center align-center w-full",
              addButton?.className,
            )}
            onClick={addButton.handleClick}
          >
            {addButton.icon ? (
              <Icons name={addButton.icon} size={18} color='#fff' />
            ) : (
              <PlusIcon size={18} strokeWidth={1.8} className='mx-1' />
            )}
            <p className={`${addButton.icon && "px-2"}`}>{addButton?.label}</p>
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      <Select
        value={field?.value}
        onValueChange={(value) =>
          onFieldValueChange
            ? onFieldValueChange(value)
            : field?.onChange(value)
        }
        defaultValue={defaultValue}
      >
        <Comp>
          <SelectTrigger className={cn("", className)} isArrow={!customTrigger}>
            {customTrigger ? (
              customTrigger
            ) : (
              <SelectValue placeholder={placeholder} />
            )}
          </SelectTrigger>
        </Comp>
        <SelectContent>
          {searchable && (
            <Command className='mb-2 '>
              <CommandInput
                placeholder='Search...'
                className='h-8'
                value={searchedText}
                onValueChange={setSearchedText}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              />
            </Command>
          )}
          {getContent()}
        </SelectContent>
      </Select>
      {/* <FormMessage /> */}
    </>
  );
};

type OmmitedMultiSelectProps = Omit<MultiSelectProps, "onValueChange">;
interface SelectMultiProps extends OmmitedMultiSelectProps {
  control?: Control<TAny>;
  name?: string;
  label?: string;
  isRequired?: boolean;
  defaultValue?: string[];
  node?: {
    position: "right";
    node: React.ComponentType<{ id: string }>;
  };
  withoutForm?: {
    values: string[];
    onValueChange: (value: LabelValue[]) => void;
  };
  maxCount?: number;
}

const SelectMulti = (props: SelectMultiProps): React.JSX.Element => {
  const {
    options,
    placeholder,
    isLoading,
    defaultValue,
    name,
    label,
    isRequired,
    maxCount = 2,
    ...rest
  } = props;

  if (props.withoutForm) {
    return (
      <section className='space-y-2'>
        {label && (
          <Label
            required={isRequired}
            className='font-normal flex items-start gap-x-[0.5px] pl-1'
          >
            {label}
          </Label>
        )}
        <SelectMultiple
          placeholder={placeholder}
          defaultValue={props.withoutForm.values}
          // Values={props.withoutForm.values}
          options={options}
          isLoading={isLoading}
          onValueChange={(value) => props.withoutForm?.onValueChange(value)}
          maxCount={maxCount}
          {...rest}
        />
      </section>
    );
  }

  return (
    <FormField
      name={name || ""}
      control={props.control}
      render={({ field }) => (
        <FormWrapper label={label} isRequired={isRequired}>
          <SelectMultiple
            placeholder={placeholder}
            defaultValue={defaultValue || []}
            Values={field.value}
            options={options}
            isLoading={isLoading}
            onValueChange={(value) => field.onChange(value)}
            maxCount={maxCount}
            {...rest}
          />
        </FormWrapper>
      )}
    />
  );
};

// New Component
const FormSelectSingle = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: SelectSingle<TFieldValues, TName> & {
    withoutForm?: boolean;
    popoverClassName?: string;
  },
): React.JSX.Element => {
  const {
    placeholder,
    options,
    field,
    className,
    popoverClassName,
    isLoading,
    onFieldValueChange,
    defaultValue,
    withoutForm,
    addButton,
    deleteAction,
    disabled,
    groupedOptions,
    side,
  } = props;
  const [open, setOpen] = useState(false);
  const Comp = withoutForm ? React.Fragment : FormControl;

  const getSelectedOption = useCallback(
    (options: Option[]): Option | undefined => {
      return options?.find((option) => {
        const optionValue =
          typeof option?.value === "object" ? option?.value?.id : option?.value;

        if (field?.value) {
          return optionValue === field?.value;
        }

        return optionValue === field?.value || optionValue === defaultValue;
      });
    },
    [field?.value, defaultValue],
  );

  const selectedLabel = useMemo((): string | undefined => {
    const selectedOption = groupedOptions
      ? getSelectedOption(groupedOptions?.flatMap((group) => group.options))
      : getSelectedOption(options || []);

    return selectedOption?.label || undefined;
  }, [options, groupedOptions, getSelectedOption]);

  const handleSelect = useCallback(
    (currentLabel: string, optionValue: string, index: number) => {
      if (onFieldValueChange) {
        onFieldValueChange(
          currentLabel.toLocaleLowerCase() ===
            selectedLabel?.toLocaleLowerCase()!.concat(index.toString())
            ? ""
            : optionValue,
          currentLabel,
        );
      } else {
        field?.onChange(
          currentLabel.toLowerCase() ===
            selectedLabel?.toLowerCase()!.concat(index.toString())
            ? ""
            : optionValue,
        );
      }
      setOpen(false);
    },
    [onFieldValueChange, selectedLabel, field],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === "Tab") {
      setOpen(true);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const renderUserIcon = (url: string): JSX.Element => {
    return (
      <img
        src={url}
        alt='user'
        className='object-cover w-8 h-8 mr-2 rounded-md'
      />
    );
  };

  const renderOptions = (
    options: Option[],
    className?: string,
  ): JSX.Element => {
    return (
      <>
        {options?.map((option, index) => {
          const optionValue =
            typeof option?.value === "object"
              ? option?.value?.id
              : option?.value;

          //  this is for css injection
          const escapedValue = CSS.escape(option?.label);

          return (
            <CommandItem
              key={optionValue}
              value={escapedValue + index}
              onSelect={(currentLabel) => {
                handleSelect(currentLabel, optionValue, index);
              }}
              className={cn(
                "p-2 content-stretch group cursor-pointer text-sm",
                className,
              )}
            >
              {option?.url && renderUserIcon(option?.url)}
              <span className='line-clamp-2' title={option?.label}>
                {option?.label}
              </span>
              {deleteAction && (
                <div
                  className='absolute invisible p-1 cursor-pointer right-3 top-2 group-hover:visible'
                  onClick={(e) => {
                    e.preventDefault();
                    deleteAction(optionValue);
                  }}
                >
                  <Trash2 name='trash' color='red' size={18} />
                </div>
              )}
            </CommandItem>
          );
        })}
      </>
    );
  };

  return (
    <Popover.Popover open={open} onOpenChange={setOpen}>
      <Comp>
        <Popover.PopoverTriggerButton
          className={cn(className)}
          onKeyUp={handleKeyDown}
          disabled={disabled}
          // defaultValue={defaultValue}
          side={side}
        >
          {selectedLabel ? (
            selectedLabel
          ) : (
            <span className='opacity-30'>{placeholder}</span>
          )}
        </Popover.PopoverTriggerButton>
      </Comp>
      <Popover.PopoverContent
        className='p-2 popover-content-width-full -mt-2 border border-input  rounded-[0.5rem] shadow-select z-[60] min-w-fit'
        portal={false}
        align='start'
        side={side}
      >
        <Command
          filter={(value, search) =>
            value.includes(search.toLowerCase()) ? 1 : 0
          }
          // defaultValue={defaultValue}
          className={popoverClassName}
        >
          <CommandList>
            {options?.length !== 0 && (
              <CommandInput placeholder='Search here' />
            )}
            <CommandEmpty firstRender={true}>No options</CommandEmpty>
            {isLoading ? (
              <CommandLoading>
                <Loader2Icon className='w-6 h-6 animate-spin' />
              </CommandLoading>
            ) : (
              <ScrollArea className='overflow-y-scroll max-h-44 select-scrollbar-hide'>
                {groupedOptions ? (
                  groupedOptions?.map((group) => (
                    <CommandGroup
                      key={group?.header}
                      heading={group?.header}
                      className='[&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:text-black
                       px-0 !mx-0 '
                    >
                      {renderOptions(group?.options, "pl-6")}
                    </CommandGroup>
                  ))
                ) : (
                  <CommandGroup className=' px-0 !mx-0'>
                    {renderOptions(options || [])}
                  </CommandGroup>
                )}
              </ScrollArea>
            )}
          </CommandList>
          {addButton && (
            <Button
              type='button'
              className={cn(
                "flex justify-center items-center align-center w-full bg-[#F2F4F7] text-sm tracking-wide space-x-2 font-medium ",
                addButton.className,
              )}
              variant={addButton?.variant || "secondary"}
              onClick={addButton.handleClick}
            >
              {addButton.icon ? (
                <Icons name={addButton.icon} size={18} color='#fff' />
              ) : (
                <PlusIcon size={18} strokeWidth={1.8} className='mx-1' />
              )}
              <p className={addButton.icon && "px-3 "}>{addButton?.label}</p>
            </Button>
          )}
        </Command>
      </Popover.PopoverContent>
    </Popover.Popover>
  );
};

export { FormSelectSingle, SelectMulti, FormSelectSingles };
